
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  FilterBar,
  FilterState,
  PrayerList,
  PrayerEditor,
} from '@/components/moderation';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui';
import { useAuth } from '@/lib/auth-context';
import { useBranding } from '@/lib/branding-context';
import { usePrayerRequests } from '@/hooks/usePrayerRequests';
import { PrayerRequest, PrayerStatus, UpdatePrayerRequest } from '@/types';

export default function SubmissionsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [token, setToken] = useState<string | undefined>(undefined);
  const [editingPrayer, setEditingPrayer] = useState<PrayerRequest | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const initialStatus = searchParams.get('status') as PrayerStatus | null;
  const selectedId = searchParams.get('id');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedToken = localStorage.getItem('access_token');
      if (storedToken) {
        setToken(storedToken);
      }
    }
  }, []);

  const initialFilters: Partial<FilterState> = useMemo(() => ({
    status: initialStatus ?? PrayerStatus.PENDING,
    sortOrder: 'asc',
  }), [initialStatus]);

  const { user } = useAuth();
  const { branding } = useBranding();
  const responsesEnabled = branding.responsesEnabled !== false;
  const stageEnabled = branding.stageEnabled === true;
  const currentUserId = user?.id ?? null;

  const {
    prayers,
    total,
    page,
    totalPages,
    isLoading,
    error,
    filters,
    setFilters,
    setPage,
    refresh,
    updatePrayer,
    dismissPrayer,
    queuePrayer,
    sendToStage,
    restorePrayer,
    requestResponse,
    reportPrayer,
    isUpdating,
    queuingIds,
    stagingIds,
    editingBy,
    respondingBy,
    sendMessage,
  } = usePrayerRequests({
    initialFilters,
    pageSize: 20,
    token,
    autoFetch: true,
    enableRealtime: true,
  });

  const editingPrayerIdRef = useRef<string | null>(null);

  const handleEdit = useCallback(
    (prayer: PrayerRequest) => {
      setEditingPrayer(prayer);
      setIsEditorOpen(true);
      editingPrayerIdRef.current = prayer.id;
      sendMessage({ action: 'start_editing', prayer_id: prayer.id });
    },
    [sendMessage]
  );

  const handleSave = useCallback(
    async (id: string, data: UpdatePrayerRequest) => {
      const success = await updatePrayer(id, data);
      if (success) {
        sendMessage({ action: 'stop_editing', prayer_id: id });
        editingPrayerIdRef.current = null;
        setIsEditorOpen(false);
        setEditingPrayer(null);
      }
    },
    [updatePrayer, sendMessage]
  );

  const handleQueue = useCallback(
    async (prayer: PrayerRequest, queueType: 'prayer' | 'praise') => {
      await queuePrayer(prayer.id, queueType);
    },
    [queuePrayer]
  );

  const handleSendToStage = useCallback(
    async (prayer: PrayerRequest) => {
      await sendToStage(prayer.id);
    },
    [sendToStage]
  );

  const handleDismiss = useCallback(
    async (prayer: PrayerRequest) => {
      if (confirm('Are you sure you want to dismiss this prayer request?')) {
        await dismissPrayer(prayer.id);
      }
    },
    [dismissPrayer]
  );

  const handleRestore = useCallback(
    async (prayer: PrayerRequest) => {
      await restorePrayer(prayer.id);
    },
    [restorePrayer]
  );

  const handleRequestResponse = useCallback(
    async (prayer: PrayerRequest) => {
      await requestResponse(prayer.id);
    },
    [requestResponse]
  );

  const handleReport = useCallback(
    async (prayer: PrayerRequest) => {
      const reason = window.prompt(
        'Optional: briefly describe why this submission is inappropriate.'
      );
      if (reason === null) return;
      await reportPrayer(prayer.id, reason.trim() || undefined);
    },
    [reportPrayer]
  );

  const handleFilterChange = useCallback(
    (newFilters: FilterState) => {
      setFilters(newFilters);
    },
    [setFilters]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setPage]
  );

  useEffect(() => {
    if (selectedId && prayers.length > 0) {
      const prayer = prayers.find((p) => p.id === selectedId);
      if (prayer) {
        handleEdit(prayer);
      }
    }
  }, [selectedId, prayers, handleEdit]);

  useEffect(() => {
    return () => {
      const prayerId = editingPrayerIdRef.current;
      if (prayerId) {
        sendMessage({ action: 'stop_editing', prayer_id: prayerId });
      }
    };
  }, [sendMessage]);

  const baseUrl = '/submissions';

  return (
    <div className="space-y-6">
      <div className="mx-auto max-w-7xl">
        <PageHeader
          title="Submissions"
          description={`${total} total submissions • Page ${page} of ${totalPages || 1}`}
          actions={
            <Button
              variant="outline"
              onClick={() => refresh()}
              disabled={isLoading}
            >
              <svg
                className="mr-2 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </Button>
          }
        />

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-900/20">
            <div className="flex items-center gap-2">
              <svg
                className="h-5 w-5 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                {error}
              </p>
            </div>
          </div>
        )}

        <div className="mb-6">
          <FilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            isLoading={isLoading}
            showResponseFilter={responsesEnabled}
          />
        </div>

        <PrayerList
          prayers={prayers}
          isLoading={isLoading}
          onEdit={handleEdit}
          onQueue={handleQueue}
          onDismiss={handleDismiss}
          onReport={handleReport}
          onRestore={handleRestore}
          onRequestResponse={responsesEnabled ? handleRequestResponse : undefined}
          onSendToStage={stageEnabled ? handleSendToStage : undefined}
          listContext="submissions"
          responsesEnabled={responsesEnabled}
          stageEnabled={stageEnabled}
          userRole={user?.role}
          selectedId={selectedId || undefined}
          editingBy={editingBy}
          respondingBy={respondingBy}
          currentUserId={currentUserId}
          queuingIds={queuingIds}
          stagingIds={stagingIds}
        />

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-6 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, total)} of{' '}
              {total} results
            </p>
            <nav className="flex items-center gap-2" aria-label="Pagination">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1 || isLoading}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                <span className="sr-only sm:not-sr-only sm:ml-1">Previous</span>
              </Button>

              <div className="hidden items-center gap-1 sm:flex">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={isLoading}
                      className={`flex h-8 w-8 items-center justify-center rounded text-sm font-medium transition-colors
                        ${
                          page === pageNum
                            ? 'bg-primary-600 text-white'
                            : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700'
                        }
                        disabled:opacity-50`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <span className="text-sm text-slate-600 dark:text-slate-400 sm:hidden">
                {page} / {totalPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages || isLoading}
              >
                <span className="sr-only sm:not-sr-only sm:mr-1">Next</span>
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Button>
            </nav>
          </div>
        )}

        <PrayerEditor
          prayer={editingPrayer}
          isOpen={isEditorOpen}
          onClose={() => {
            const prayerId = editingPrayer?.id ?? editingPrayerIdRef.current;
            if (prayerId) {
              sendMessage({ action: 'stop_editing', prayer_id: prayerId });
              editingPrayerIdRef.current = null;
            }
            setIsEditorOpen(false);
            setEditingPrayer(null);
            if (selectedId) {
              const newParams = new URLSearchParams(searchParams.toString());
              newParams.delete('id');
              const newUrl = newParams.toString()
                ? `${baseUrl}?${newParams.toString()}`
                : baseUrl;
              navigate(newUrl, { preventScrollReset: true });
            }
          }}
          onSave={handleSave}
          isSaving={isUpdating}
        />
      </div>
    </div>
  );
}
